import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Empty, Modal, Space, Table, Tag, Typography, message } from "antd";

import { measurementLabel } from "./writes/measurementWrites";
import {
  clearSimulatorWrites,
  listSimulatorWrites,
  TABLES_WITHOUT_DELETE_TRIGGER,
  type SimulatorWriteEntry,
} from "./writes/simulatorWriteLog";

const TABLE_LABEL: Record<string, string> = {
  client_workout_activity: "Trening",
  client_nutrition_day: "Dieta",
  habit_completion: "Nawyk",
  challenge_task_completion: "Zadanie wyzwania",
  measurement: "Pomiar",
  exercise_recording: "Nagranie",
};

const SimulatorCleanupPanel = ({ traineeId }: { traineeId: string }) => {
  const queryClient = useQueryClient();
  const logKey = ["simulator", "log", traineeId];

  const log = useQuery({
    queryKey: logKey,
    queryFn: () => listSimulatorWrites(traineeId),
  });

  const cleanup = useMutation({
    mutationFn: () => clearSimulatorWrites(traineeId),
    onSuccess: (result) => {
      void message.success(`Usunięto ${result.deletedRows}, przywrócono ${result.revertedRows} wpisów`);
      if (result.vanishedRows) {
        void message.warning(`${result.vanishedRows} wierszy zniknęło przed przywróceniem — nie było czego cofnąć`);
      }
      if (result.strandedRows) {
        void message.warning(
          `${result.strandedRows} wierszy zmienionych bez zapisanego stanu sprzed — zostały bez zmian`,
        );
      }
      if (result.staleCounterTables.length) {
        void message.warning(
          "Dieta i pomiary nie mają triggera na usunięcie — liczniki aktywności trzeba przeliczyć osobno",
        );
      }
      void queryClient.invalidateQueries({ queryKey: ["simulator"] });
    },
    onError: (error: Error) => void message.error(error.message),
  });

  const entries = log.data ?? [];

  const confirmCleanup = () => {
    Modal.confirm({
      title: "Usunąć wszystkie dane wygenerowane przez symulator?",
      content: `Skasowanych zostanie ${entries.length} wpisów. Danych wprowadzonych przez samą klientkę to nie dotyczy.`,
      okText: "Usuń",
      okButtonProps: { danger: true },
      cancelText: "Anuluj",
      onOk: () => cleanup.mutateAsync(),
    });
  };

  return (
    <Card
      size="small"
      title="Dane wygenerowane przez symulator"
      extra={
        <Button
          danger
          size="small"
          disabled={entries.length === 0}
          loading={cleanup.isLoading}
          onClick={confirmCleanup}
        >
          Wyczyść dane symulatora
        </Button>
      }
    >
      <Space direction="vertical" size={12} className="w-full">
        <Alert
          type="info"
          showIcon
          message="Rejestr trzymamy osobno, poza samymi encjami"
          description={`Wpisy symulatora są nieodróżnialne od danych klientki, bo mają być — dlatego id wygenerowanych wierszy lądują w simulator_write_log. Usunięcie cofa punkty tam, gdzie trigger reaguje na DELETE; w tabelach ${TABLES_WITHOUT_DELETE_TRIGGER.join(" i ")} liczniki zostaną nieprzeliczone.`}
        />

        {entries.length ? (
          <Table<SimulatorWriteEntry>
            size="small"
            rowKey="id"
            dataSource={entries}
            pagination={{ pageSize: 8, hideOnSinglePage: true }}
            columns={[
              {
                title: "Obszar",
                dataIndex: "tableName",
                render: (tableName: string) => <Tag>{TABLE_LABEL[tableName] ?? tableName}</Tag>,
              },
              {
                title: "Dzień",
                render: (_value, entry) => entry.metadata?.eventDate ?? "—",
              },
              {
                title: "Co",
                // Etykieta jest zapisana w bazie, więc wpisy sprzed wprowadzenia tłumaczeń wciąż
                // trzymają surową wartość enuma — mapujemy przy renderze, nie tylko przy zapisie.
                render: (_value, entry) => (
                  <Typography.Text>
                    {entry.metadata?.label ? measurementLabel(entry.metadata.label) : "—"}
                  </Typography.Text>
                ),
              },
              {
                title: "Tryb",
                render: (_value, entry) =>
                  entry.metadata?.mode === "bulk" ? <Tag color="purple">generator</Tag> : <Tag>pojedynczo</Tag>,
              },
              {
                title: "Czyszczenie",
                render: (_value, entry) =>
                  (entry.metadata?.op ?? "insert") === "insert" ? (
                    <Tag color="red">usunie wiersz</Tag>
                  ) : (
                    <Tag color="blue">przywróci stan</Tag>
                  ),
              },
            ]}
          />
        ) : (
          <Empty description="Symulator nic jeszcze nie zapisał" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Space>
    </Card>
  );
};

export default SimulatorCleanupPanel;
